import React from 'react'
import styled from 'styled-components'
import {
  DSText,
  DSTextAndIcon,
  DSSpacer,
  DSButton,
  DSFlex,
  DSSurface,
  DSIcon,
  DSIconName,
  DSIconSize,
} from '@zoe/ds-web'
import { onNextClickType } from 'components/types'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'
import { Conditions, Quizzes } from '../../redux/quiz/types'
import { evaluateCondition } from '../utils'
import { ZiggieAsk } from '../core/ziggie/ZiggieAsk'
import { GifName } from '../../data/gifs'

export type SummaryFormatFn = 'weightWithUnit'

type SummaryItemConfig = {
  label: string
  questionKey: string
  icon?: DSIconName
  // Glyph size within the fixed 24px icon column. Icons whose artwork fills its
  // box (e.g. generic-food) need a smaller size to look the same weight as the rest.
  iconSize?: DSIconSize
  formatAnswer?: Record<string, string>
  formatFn?: SummaryFormatFn
  showWhen?: Conditions
}

export type HealthSummaryScreenProps = {
  title: string
  // Optional subtitle under the title (e11).
  description?: string
  // e10/e11: row labels in regular weight and secondary colour, with the icons in a
  // fixed-width column so they stay aligned whatever their glyph size.
  regularLabels?: boolean
  summaryItems: SummaryItemConfig[]
  // Same copy, shown with Ziggie: Ziggie says the title from a speech bubble
  // (the description isn't shown).
  withZiggie?: { gif: GifName; typewriter?: boolean }
}

type Props = HealthSummaryScreenProps & {
  onNextClick: onNextClickType
  quiz: Quizzes
  conditionValues: Record<string, string | string[] | undefined>
}

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  padding: var(--grid-16);
  gap: var(--grid-12);
`

const RowLabel = styled.div`
  flex: 1;
`

const IconLabel = styled.div`
  display: flex;
  align-items: center;
  gap: var(--grid-8);
`

/* Keeps every label starting at the same x, whatever the glyph's size. */
const IconBox = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const formatSingleWeight = (
  kg: number | undefined,
  lbs: number | undefined,
  weightUnit: string | undefined,
): string => {
  if ((weightUnit === 'lbs' || weightUnit === 'stone') && lbs) return `${Math.round(lbs)}lbs`
  if (weightUnit === 'kg' && kg) return `${Math.round(kg)}kg`
  // Fallback: show whichever value is available
  if (lbs) return `${Math.round(lbs)}lbs`
  if (kg) return `${Math.round(kg)}kg`
  return ''
}

const formatWeightWithUnit = (quiz: Quizzes, questionKey: string): string => {
  if (questionKey === 'current_weight') {
    return formatSingleWeight(quiz.weight_kg, quiz.weight_lbs, quiz.weight_unit)
  }
  if (questionKey === 'ideal_weight') {
    return formatSingleWeight(quiz.ideal_weight_kg, quiz.ideal_weight_lbs, quiz.weight_unit)
  }
  return ''
}

export const HealthSummaryScreen: React.FC<Props> = ({
  title,
  description,
  regularLabels,
  summaryItems,
  withZiggie,
  onNextClick,
  quiz,
  conditionValues,
}) => {
  const visibleItems = summaryItems.filter(
    (item) => !item.showWhen || evaluateCondition(item.showWhen, conditionValues),
  )

  const getDisplayValue = (item: SummaryItemConfig): string => {
    if (item.formatFn === 'weightWithUnit') {
      return formatWeightWithUnit(quiz, item.questionKey)
    }
    const rawValue = (quiz as Record<string, unknown>)[item.questionKey]
    if (rawValue === undefined || rawValue === null) return ''
    // Multi-select answers (e.g. diet_type, sleep_quality) are arrays: format each
    // value and join, rather than stringifying the whole array. De-dupe so options
    // that map to the same label don't show twice (e.g. "Average, Average").
    if (Array.isArray(rawValue)) {
      const formatted = rawValue.map((v) => item.formatAnswer?.[String(v)] ?? String(v))
      return Array.from(new Set(formatted)).join(', ')
    }
    const strValue = String(rawValue)
    return item.formatAnswer?.[strValue] ?? strValue
  }

  return (
    <ScreenContainer>
      {withZiggie ? (
        <ZiggieAsk gif={withZiggie.gif} text={title} quiz={quiz} typewriter={withZiggie.typewriter} />
      ) : (
        <>
          <DSText variant="fluid-heading-700" weight="semiBold" align="left">
            {title}
          </DSText>
          {description && (
            <>
              <DSSpacer size={8} direction="vertical" />
              <DSText variant="fluid-paragraph-300" color="secondary">
                {description}
              </DSText>
            </>
          )}
        </>
      )}
      <DSSpacer size={24} direction="vertical" />
      <DSFlex direction="column" gap={8}>
        {visibleItems.map((item) => (
          <DSSurface key={item.questionKey} surfaceColor="neutral" radius={12} padding={0}>
            <SummaryRow data-testid={`summary-row-${item.questionKey}`}>
              <RowLabel>
                {item.icon && regularLabels ? (
                  <IconLabel>
                    <IconBox>
                      <DSIcon name={item.icon} size={item.iconSize ?? 24} />
                    </IconBox>
                    <DSText variant="fluid-paragraph-200" weight="regular" color="secondary">
                      {item.label}
                    </DSText>
                  </IconLabel>
                ) : item.icon ? (
                  <DSTextAndIcon icon={{ name: item.icon, size: 24 }} variant="fluid-paragraph-200">
                    {item.label}
                  </DSTextAndIcon>
                ) : (
                  <DSText variant="fluid-paragraph-200" weight={regularLabels ? 'regular' : undefined}>
                    {item.label}
                  </DSText>
                )}
              </RowLabel>
              <DSText variant="fluid-paragraph-200" weight="semiBold">
                {getDisplayValue(item)}
              </DSText>
            </SummaryRow>
          </DSSurface>
        ))}
      </DSFlex>
      <StickyFooter>
        <StickyFooterContent>
          <DSButton
            variant="primary"
            colorScheme="default"
            size="large"
            width="full"
            onPress={onNextClick}
            testId="continue-button"
          >
            <DSText variant="fluid-paragraph-200" weight="semiBold">
              Continue
            </DSText>
          </DSButton>
        </StickyFooterContent>
      </StickyFooter>
    </ScreenContainer>
  )
}
